import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { BellOff, Check, Loader2, AlertTriangle } from "lucide-react";

// One-tap "turn off this alert" landing for the email link. Requires the SPA to
// run the POST (email-client link prefetchers won't trigger it accidentally).
export default function AlertUnsubscribe() {
    const [params] = useState(() => new URLSearchParams(window.location.search));
    const [state, setState] = useState("working"); // working | done | error
    const [label, setLabel] = useState("");
    const token = params.get("token") || "";
    const sid = params.get("sid") || "";

    useEffect(() => {
        // Clean the address bar so the token/sid aren't left visible.
        window.history.replaceState({}, "", "/alerts/unsubscribe");
        if (!sid) { setState("error"); return; }
        api.post(`/saved-searches/${sid}/unsubscribe${token ? `?token=${encodeURIComponent(token)}` : ""}`)
            .then((r) => { setLabel(r.data?.label || ""); setState("done"); })
            .catch(() => setState("error"));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const manageHref = token ? `/alerts?token=${encodeURIComponent(token)}` : "/alerts";

    return (
        <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="alert-unsubscribe-page">
            {state === "working" && (
                <>
                    <Loader2 className="h-8 w-8 text-[#ff4500] mx-auto mb-4 animate-spin" />
                    <div className="text-[#a0a0a0]">Turning off this alert…</div>
                </>
            )}
            {state === "done" && (
                <>
                    <div className="h-14 w-14 rounded-full bg-[#22c55e]/10 border border-[#22c55e]/40 flex items-center justify-center mx-auto mb-4">
                        <BellOff className="h-6 w-6 text-[#22c55e]" />
                    </div>
                    <h1 className="font-display text-4xl tracking-wide mb-2" data-testid="unsub-success">Alert turned off</h1>
                    <p className="text-sm text-[#a0a0a0]">
                        <Check className="h-4 w-4 text-[#22c55e] inline mr-1" />
                        You'll no longer get emails for {label ? <strong className="text-white">"{label}"</strong> : "this saved search"}.
                    </p>
                    <div className="mt-8 flex items-center justify-center gap-3">
                        <Link to={manageHref} className="bg-[#ff4500] hover:bg-[#ff5722] text-white px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold" data-testid="unsub-manage-link">
                            Manage all alerts
                        </Link>
                        <Link to="/" className="border border-[#2a2a2a] hover:border-white text-white px-6 py-3 uppercase tracking-[0.2em] text-xs">Home</Link>
                    </div>
                </>
            )}
            {state === "error" && (
                <>
                    <div className="h-14 w-14 rounded-full bg-red-500/10 border border-red-500/40 flex items-center justify-center mx-auto mb-4">
                        <AlertTriangle className="h-6 w-6 text-red-500" />
                    </div>
                    <h1 className="font-display text-4xl tracking-wide mb-2">Link expired or invalid</h1>
                    <p className="text-sm text-[#a0a0a0]">We couldn't turn off this alert. The link may have expired — open your alerts to manage them.</p>
                    <Link to="/alerts" className="mt-8 inline-block bg-[#ff4500] hover:bg-[#ff5722] text-white px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold">Go to My Alerts</Link>
                </>
            )}
        </div>
    );
}
